// React.
import { useEffect, useMemo, useState } from "react";

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import { ParameterComparisonTableProps } from "./types";
import { ParameterComparison } from '../../../types/instance/Parameter';

// Components.
import ReactTable from '../../../component/ReactTable';

// Helpers.
import { formatDateForApi, formatPeriodHeadingFromDate, fetchResults, getDefaultPageSize, getTimeZone } from "../../../helpers/utils";
import DatasourceParameterApi from '../../../api/instance/DatasourceParameterApi';
import { showError } from '../../../utils/showAlert';
import SearchInput from '../../../component/SearchInput';

const ParameterComparisonTable = (props: ParameterComparisonTableProps) => {
    const { instanceOptions, generateId } = props;

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [totalDifferences, setTotalDifferences] = useState<number>(0);
    const [data, setData] = useState<ParameterComparison[]>([]);
    const [tableFilter, setTableFilter] = useState<string>('');

    const tz = getTimeZone();

    useEffect(() => {
        async function load() {
            try {
                setLoading(true);

                const from0 = formatDateForApi(instanceOptions[0].periodStart, tz);
                const to0 = formatDateForApi(instanceOptions[0].periodEnd, tz);
                const from1 = formatDateForApi(instanceOptions[1].periodStart, tz);
                const to1 = formatDateForApi(instanceOptions[1].periodEnd, tz);
                const id0 = instanceOptions[0].instanceId;
                const id1 = instanceOptions[1].instanceId;

                const promises: Promise<any>[] =
                        [
                            DatasourceParameterApi.fetchComparison(tz, from0, to0, id0, from1, to1, id1)
                        ];

                const result: any[][] = await fetchResults(promises);

                if (active) {
                    const dataWithDiff = result[0].map((row) => ({
                        ...row,
                        isDifferent: row.value1 !== row.value2, // Add the isDifferent property
                    }))

                    const totalDifferences = dataWithDiff.filter((row) => row.isDifferent).length;

                    setData(dataWithDiff);
                    setTotalDifferences(totalDifferences);
                    setLoading(false);
                }
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load parameter comparison', errorMessage);
                setData([]);
                setLoading(false);
            }
        }

        let active: boolean = true;
        load();
        return function () {
            active = false;
        }
    }, [generateId]);


    const columns: Column[] = useMemo(() => {
        return [
            {
                Header: 'Parameter',
                accessor: 'parameter',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'string'
            },
            {
                Header: (
                        <div className="text-center d-inline-block">
                            {props.instanceOptions[0].instanceName}
                            <br/>
                            ({formatPeriodHeadingFromDate(props.instanceOptions[0].periodStart, props.instanceOptions[0].periodEnd, tz)})
                        </div>
                ),
                accessor: 'value1',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'string'
            },
            {
                Header: (
                        <div className="text-center d-inline-block">
                            {props.instanceOptions[1].instanceName}
                            <br/>
                            ({formatPeriodHeadingFromDate(props.instanceOptions[1].periodStart, props.instanceOptions[1].periodEnd, tz)})
                        </div>
                ),
                accessor: 'value2',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'string'
            },
            {
                Header: 'Is Different',
                accessor: 'isDifferent',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic',
                Cell: ({ value }) => (value ? 'Yes' : 'No')
            }
        ]
    }, [data]);

    const tableData = useMemo(() =>
                    data.filter((row) =>
                            row.parameter.toLowerCase().includes(tableFilter.toLowerCase()) ||
                            row.value1?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                            row.value2?.toLowerCase().includes(tableFilter.toLowerCase())),
            [data, tableFilter]);

    // Transform function to escape quotes and convert boolean for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        const transformed = JSON.parse(JSON.stringify(csvData));
        transformed.forEach((item: any) => {
            if (item.value1) {
                item.value1 = item.value1.replaceAll('"', '""');
            }
            if (item.value2) {
                item.value2 = item.value2.replaceAll('"', '""');
            }
            // Convert isDifferent boolean to 'Yes' or 'No' for CSV export (if still boolean)
            if (item.isDifferent !== undefined && typeof item.isDifferent === 'boolean') {
                item.isDifferent = item.isDifferent ? 'Yes' : 'No';
            }
        });
        return transformed;
    }, []);

    const tableInstance = useTable(
            {
                columns,
                data: tableData,
                initialState:
                        {
                            pageIndex: 0,
                            pageSize: getDefaultPageSize()
                        },
                disableSortRemove: true,
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            }, useSortBy, usePagination);

    const
            {
                getTableProps,
                getTableBodyProps,
                headerGroups,
                prepareRow,
                page,
                setPageSize,
                pageOptions,
                pageCount,
                canPreviousPage,
                previousPage,
                canNextPage,
                nextPage,
                gotoPage,
                state: { pageIndex, pageSize }
            } = tableInstance;

    function clearTableFilter() {
        setTableFilter('');
    }

    return <>
        <div className="card">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                Parameters
                <span className="badge bg-info"
                      data-tooltip-id="global-tooltip" data-tooltip-content="Total Parameters">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/> <span
                    className='warning-message'>({totalDifferences} {totalDifferences === 1 ? 'difference' : 'differences'})</span>
            </div>
            <div id="collapse-usage" className="card-body collapse show">
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                                tableInstance={tableInstance}
                                filename="Parameters"
                                transformData={transformCsvData}
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                                autoFocus
                        />
                    </div>
                </div>
                <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={error}
                        loading={loading}
                        getRowProps={(row) => ({ className: (row.original as any).isDifferent ? 'row-different' : '' })}
                />
            </div>
        </div>
    </>
}

export default ParameterComparisonTable;
